import { createContext, type ReactNode, useCallback, useEffect, useState } from "react";

// Table density preference — controls vertical row padding and header height
// across every table in the app. Persisted to localStorage so the choice
// survives page refreshes and follows the user across tabs.
//
// Reading the value: use `useDensity()` for the current value, or
// `useDensityClasses()` for the small set of Tailwind utilities the Table
// primitives apply. Setting: `useDensityActions().setDensity(next)`.

export type Density = "comfortable" | "compact";

const STORAGE_KEY = "c360:density";

function readInitialDensity(): Density {
  if (typeof window === "undefined") return "comfortable";
  const v = window.localStorage.getItem(STORAGE_KEY);
  return v === "compact" ? "compact" : "comfortable";
}

type DensityValue = {
  density: Density;
  setDensity: (next: Density) => void;
};

const DensityContext = createContext<DensityValue>({
  density: "comfortable",
  setDensity: () => {},
});

export function DensityProvider({ children }: { children: ReactNode }) {
  const [density, setDensityState] = useState<Density>(readInitialDensity);

  // Sync to localStorage whenever the value changes so refreshes pick it up
  // and the storage event below fires for other tabs.
  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(STORAGE_KEY, density);
  }, [density]);

  // Cross-tab sync — if the user flips density in one tab, the others update
  // without a refresh. Filter to our key so unrelated storage writes are
  // ignored.
  useEffect(() => {
    if (typeof window === "undefined") return;
    function onStorage(e: StorageEvent) {
      if (e.key !== STORAGE_KEY) return;
      const next = e.newValue === "compact" ? "compact" : "comfortable";
      setDensityState(next);
    }
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const setDensity = useCallback((next: Density) => setDensityState(next), []);

  return (
    <DensityContext.Provider value={{ density, setDensity }}>{children}</DensityContext.Provider>
  );
}

export { DensityContext };
